import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { InvoicePreview } from "@/pages/InvoiceDetail";
import { CreditCard, CheckCircle2, XCircle, Loader2, FileDown } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50">
    <header className="bg-white border-b border-slate-100">
      <div className="max-w-4xl mx-auto px-6 py-3.5">
        <Link to="/" data-testid="public-invoice-logo" aria-label="NEXA — Accueil">
          <Logo />
        </Link>
      </div>
    </header>
    <main className="max-w-4xl mx-auto px-6 py-10">{children}</main>
  </div>
);

export default function PublicInvoice() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [payError, setPayError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api
      .get(`/business/public/invoices/${token}`)
      .then((r) => setInvoice(r.data))
      .catch((err) => setError(err?.response?.status || 500));
  }, [token]);

  const pay = async () => {
    setPayError("");
    setPaying(true);
    try {
      const res = await api.post(`/business/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.approval_url;
    } catch (err) {
      setPayError(formatApiError(err));
      setPaying(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <div data-testid="public-invoice-not-found" className="text-center py-24 text-slate-500">
          Facture introuvable ou lien expiré.
        </div>
      </Shell>
    );
  }

  if (!invoice) {
    return (
      <Shell>
        <div className="flex justify-center py-24">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="public-invoice-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <p className="text-sm text-slate-500">
            Facture émise par <span className="font-semibold text-brand-navy">{invoice.issuer_name}</span> via NEXA
          </p>
          <a
            data-testid="public-invoice-pdf-link"
            href={`${API_BASE}/api/business/public/invoices/${token}/pdf`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-100"
          >
            <FileDown className="w-3.5 h-3.5" /> Télécharger le PDF
          </a>
        </div>

        {invoice.status === "payee" && (
          <div data-testid="public-invoice-paid-banner" className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
            <CheckCircle2 className="w-4 h-4" /> Cette facture a été payée. Merci !
          </div>
        )}
        {invoice.status === "annulee" && (
          <div data-testid="public-invoice-cancelled-banner" className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <XCircle className="w-4 h-4" /> Cette facture a été annulée par l'émetteur.
          </div>
        )}
        {payError && (
          <div data-testid="public-invoice-pay-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{payError}</div>
        )}

        <InvoicePreview invoice={invoice} issuerName={invoice.issuer_name} />

        {(invoice.status === "envoyee" || invoice.status === "brouillon") && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6 text-center">
            <button
              data-testid="public-invoice-pay-button"
              onClick={pay}
              disabled={paying}
              className="inline-flex items-center justify-center gap-2 bg-brand-blue text-white font-semibold rounded-lg px-8 py-3.5 hover:bg-brand-navy transition-colors disabled:opacity-50"
            >
              <CreditCard className="w-4 h-4" />
              {paying ? "Redirection vers PayPal…" : `Payer ${invoice.total} $ ${invoice.currency} avec PayPal`}
            </button>
            <p className="text-xs text-slate-400 mt-3">Paiement sécurisé par PayPal — aucun compte NEXA requis.</p>
          </div>
        )}
      </div>
    </Shell>
  );
}

export function PublicInvoiceSuccess() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const hasCaptured = useRef(false);
  const paypalOrderId = searchParams.get("token") || "";

  useEffect(() => {
    if (hasCaptured.current) return;
    hasCaptured.current = true;
    if (!paypalOrderId) {
      setStatus("error");
      setError("Référence de paiement manquante.");
      return;
    }
    const capture = async () => {
      try {
        const res = await api.post(`/business/public/invoices/${token}/capture/${paypalOrderId}`);
        setStatus(res.data.payment_status === "paid" ? "paid" : "pending");
      } catch (err) {
        setStatus("error");
        setError(formatApiError(err));
      }
    };
    capture();
  }, [token, paypalOrderId]);

  return (
    <Shell>
      <div data-testid="public-invoice-success-page" className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 p-10 text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 text-brand-turquoise animate-spin mx-auto mb-4" />
            <p className="text-brand-navy font-medium">Confirmation du paiement en cours…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-brand-turquoise mx-auto mb-4" />
            <h1 data-testid="public-invoice-success-title" className="text-xl font-bold text-brand-navy mb-2">Paiement confirmé</h1>
            <p className="text-sm text-slate-500 mb-6">La facture a été marquée payée et l'émetteur a été notifié. Merci !</p>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-10 h-10 text-brand-orange animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-brand-navy mb-2">Paiement en cours de traitement</h1>
            <p className="text-sm text-slate-500 mb-6">PayPal finalise la transaction.</p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 data-testid="public-invoice-error-title" className="text-xl font-bold text-red-600 mb-2">Erreur</h1>
            <p data-testid="public-invoice-error-message" className="text-sm text-slate-500 mb-6">{error}</p>
          </>
        )}
        <Link
          data-testid="public-invoice-success-back"
          to={`/facture/${token}`}
          className="inline-block w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
        >
          Voir la facture
        </Link>
      </div>
    </Shell>
  );
}

export function PublicInvoiceCancel() {
  const { token } = useParams();
  return (
    <Shell>
      <div data-testid="public-invoice-cancel-page" className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <XCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-brand-navy mb-2">Paiement annulé</h1>
        <p className="text-sm text-slate-500 mb-6">Aucun montant n'a été débité. Vous pouvez reprendre le paiement à tout moment.</p>
        <Link
          data-testid="public-invoice-cancel-back"
          to={`/facture/${token}`}
          className="inline-block w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors"
        >
          Retour à la facture
        </Link>
      </div>
    </Shell>
  );
}
